/**
 * NFC Scan Page Wrapper (Story 3.7.c - Smart Orchestrator)
 *
 * CRITICAL: Single API Call Guarantee
 * - Fetches scan context ONCE on mount using Redux thunk
 * - Checks if data already exists in Redux before fetching (prevents refetch)
 * - All child pages (PublicScanPage, PairingPage) read from Redux (no duplicate calls)
 *
 * SMART ROUTING LOGIC (based on authentication + scan context type):
 * 1. Unauthenticated + Unclaimed → Redirect to /auth/login (store returnUrl)
 * 2. Unauthenticated + Claimed → PublicScanPage (show public profile)
 * 3. Authenticated + Any type → Route to appropriate child page
 *
 * Data Flow:
 * User scans NFC → NfcScanPage (fetches ONCE) → Redux publicScanSlice
 *                                                        ↓
 *                                               [PublicScanPage | PairingPage]
 *                                                        ↓
 *                                               (reads from Redux)
 */

import { useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchPublicScanContext,
  selectScanContext,
  selectScanLoading,
  selectScanError,
  clearScanContext,
} from "@/store/slices/publicScanSlice";
import { selectIsAuthenticated, selectUser, setReturnUrl } from "@/store/slices/authSlice";
import {
  isClaimedItem,
  isUnclaimedItem,
  isStolenItem,
} from "@/domain/types/ViewerNfcScanContext";
import PublicScanPage from "./PublicScanPage";
import PairingPage from "../Pairing/PairingPage";
import {
  ErrorState,
  PairingFormSkeleton,
  PairingItemStolenError,
  FraudulentItemError,
  AutoRedirectWithMessage,
  PartnerEventAnimation,
} from "@/ui/components";
import { AppLayout } from "@/ui/layouts";
import { parseNfcIdentifier } from "@/utils/nfcUrlParser";

/**
 * NfcScanPage Component
 *
 * The SINGLE ENTRY POINT for NFC scans.
 * Guarantees ONLY ONE API call per scan by coordinating Redux state.
 *
 * Responsive Design:
 * - Loading skeleton uses same container as child pages for consistent UX
 * - Mobile (< 768px): Full width with 32px horizontal padding
 * - Tablet+ (≥ 768px): 700px max-width (md:max-w-[43.75rem]), centered
 */
export default function NfcScanPage() {
  const { productId, itemId, signature } = useParams<{
    productId: string;
    itemId: string;
    signature: string;
  }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Redux state - shared with all child pages
  const scanContext = useAppSelector(selectScanContext);
  const loading = useAppSelector(selectScanLoading);
  const error = useAppSelector(selectScanError);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const currentUser = useAppSelector(selectUser);
  const { t } = useTranslation();

  // Single API call guarantee - only fetch if NOT already in Redux
  useEffect(() => {
    // CRITICAL: Check if data already exists before fetching
    // Prevents duplicate API calls when navigating between pages
    // CRITICAL: Don't retry on error - prevents infinite loop
    if (
      !scanContext &&
      !loading &&
      !error &&
      productId &&
      itemId &&
      signature
    ) {
      dispatch(fetchPublicScanContext({ productId, itemId, signature }));
    }
  }, [dispatch, productId, itemId, signature, scanContext, loading, error]);

  // Clear scan context when navigating to different NFC URL
  // This ensures fresh data fetch for each NFC item
  useEffect(() => {
    return () => {
      dispatch(clearScanContext());
    };
  }, [productId, itemId, signature]);

  // Loading state - show skeleton while fetching scan context
  if (loading) {
    return (
      <AppLayout>
        <PairingFormSkeleton />
      </AppLayout>
    );
  }

  // Error state - check BEFORE null check to avoid blank page on API failures
  if (error) {
    // Check if error is fraudulent (403 Invalid Signature)
    // Backend returns: "This item could not be authenticated" (403 status)
    const isFraudulentError =
      error.includes("fraudulent") ||
      error.includes("Invalid signature") ||
      error.includes("INVALID_SIGNATURE") ||
      error.includes("could not be authenticated");

    if (isFraudulentError) {
      // Show fraudulent item error (no retry button)
      return (
        <AppLayout>
          <FraudulentItemError
            nfcId={
              productId && itemId
                ? parseNfcIdentifier(`${productId}/${itemId}`)
                : undefined
            }
            errorMessage={t(
              "pairing.errors.productFraudulent",
              "This item could not be authenticated and look fraudulent. Don't hesitate to contact us to report it"
            )}
          />
        </AppLayout>
      );
    }

    // Generic error state (with retry button)
    return (
      <AppLayout>
        <ErrorState
          message={error}
          onRetry={() =>
            // @ts-expect-error - Params validated earlier in component, safe to pass
            dispatch(fetchPublicScanContext({ productId, itemId, signature }))
          }
        />
      </AppLayout>
    );
  }

  // Wait for scan context to be loaded
  // (null check ensures we don't render children without data)
  if (!scanContext) {
    return null;
  }

  // ROUTE 0: Partner Event Animation (Story 3.8.c AC #8-12)
  // Shows branded animation sequence ONLY for active events (eventUrl)
  // partnerLink (permanent campaign) follows normal flow - no animation
  // Works for both authenticated and unauthenticated users
  // Only claimed items have partner event fields
  if (isClaimedItem(scanContext) && scanContext.eventUrl) {
    return (
      <PartnerEventAnimation
        redirectUrl={scanContext.eventUrl}
        eventName={scanContext.eventName}
        partnerLogo={scanContext.partnerLogo}
      />
    );
  }

  // AUTHENTICATION ROUTING (Story 3.7.c AC #1, AC #3)
  // Unauthenticated users can only view claimed items (public profiles)
  // Unclaimed items require authentication (pairing flow)
  if (!isAuthenticated && isUnclaimedItem(scanContext)) {
    // Store returnUrl for post-login redirect
    const returnUrl = `/nfc/${productId}/${itemId}/${signature}`;
    dispatch(setReturnUrl(returnUrl));

    // Redirect to login page
    navigate("/auth/login");
    return null;
  }

  // Smart routing based on type guards (discriminated union)
  // Type-safe routing - no runtime type checking needed

  // ROUTE 1: Unclaimed item (authenticated user) → PairingPage
  // Shows pairing form for owner to claim product
  if (isUnclaimedItem(scanContext)) {
    return <PairingPage />;
  }

  // ROUTE 2: Stolen/Lost item → Display error directly (no navigation to PairingPage)
  // Shows stolen/lost warning without additional routing
  if (isStolenItem(scanContext)) {
    return (
      <AppLayout>
        <PairingItemStolenError
          productTitle={scanContext.productInfo.title}
          productImage={scanContext.productInfo.imageUrl}
          nfcId={parseNfcIdentifier(scanContext.nfcId)}
          errorMessage={
            scanContext.type === "stolen"
              ? t("pairing.errors.productStolen")
              : t("pairing.errors.productLost")
          }
        />
      </AppLayout>
    );
  }

  // ROUTE 3: Claimed item → Check ownership
  // userId is now ALWAYS present (backend change), so we can always check ownership
  if (isClaimedItem(scanContext)) {
    // Check if current user owns this item (public OR private)
    const isOwnedByCurrentUser =
      isAuthenticated &&
      currentUser?.customUserId &&
      scanContext.ownerInfo.userId === currentUser.customUserId;

    // If user owns this item → Redirect to Trophy Room
    if (isOwnedByCurrentUser) {
      return (
        <AppLayout>
          <AutoRedirectWithMessage
            message={t("pairing.banners.alreadyOwned")}
            redirectPath="/owner/trophy-room"
            delay={2000}
          />
        </AppLayout>
      );
    }

    // If not owned by user → Show PublicScanPage (handles public/private display)
    return <PublicScanPage />;
  }

  // ROUTE 4: Invalid signature → PublicScanPage handles error display
  return <PublicScanPage />;
}
